import {
  ReactNode,
  useState,
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react';

import type {
  TableV2RecordType,
  TableV2DataObject,
} from '@frontend/components';
import { useKeyPress } from '@frontend/utils';

import { TABLE_V2_SELECT_ALL_KEY } from './constants';
import { getNestedChildKeysFromRecordDepthFirst } from './helpers';

type TableV2CheckboxSelectionContextType = {
  dataObj: TableV2DataObject;
  orderedKeys: string[];
  headerRecord: TableV2RecordType;
  selectedRows: string[];
  selectedRowsUnique: Set<string>;
  disabledOrOmittedRows: string[];
  shiftPressed: boolean;
  lastSelected: string | null;
  setLastSelected: Dispatch<SetStateAction<string | null>>;
  updateSelectedRows: (nextSelectedRows: string[]) => void;
  selectAll: () => void;
  deselectAll: () => void;
  getRowByKey: (key: string) => TableV2RecordType | undefined;
};

export const TableV2SelectionContext =
  createContext<TableV2CheckboxSelectionContextType>({
    dataObj: {},
    orderedKeys: [],
    headerRecord: { key: TABLE_V2_SELECT_ALL_KEY, children: [] },
    disabledOrOmittedRows: [],
    lastSelected: null,
    selectedRows: [],
    selectedRowsUnique: new Set(),
    setLastSelected: () => null,
    updateSelectedRows: () => null,
    shiftPressed: false,
    selectAll: () => null,
    deselectAll: () => null,
    getRowByKey: () => undefined,
  });

type TableV2SelectionContextProps = {
  inputData: TableV2DataObject;
  depthFirstOrderedKeys?: string[];
  disabledOrOmittedRows: string[];
  children: ReactNode;
};

export const TableV2SelectionProvider = ({
  inputData, // NOTE: this must be a FLAT object of the data array, with the fully nested data as the values
  depthFirstOrderedKeys,
  disabledOrOmittedRows,
  children,
}: TableV2SelectionContextProps) => {
  // when depthFirstOrderedKeys not passed in, we will assume the selection order
  // for the input data using Object.keys() is sufficient...note this may not
  // respect insertion order in situations with more complex keys
  const orderedKeys: string[] = useMemo(() => {
    const out = [TABLE_V2_SELECT_ALL_KEY];
    if (depthFirstOrderedKeys) {
      return out.concat(depthFirstOrderedKeys);
    } else {
      return out.concat(Object.keys(inputData));
    }
  }, [depthFirstOrderedKeys, inputData]);

  // this wraps the input data as children with the special ALL select key
  const headerRecord: TableV2RecordType = useMemo(() => {
    const children = [];
    for (const key of orderedKeys) {
      const child = inputData[key];
      if (child) {
        children.push(child);
      }
    }
    return { key: TABLE_V2_SELECT_ALL_KEY, children };
  }, [orderedKeys, inputData]);

  // TODO: optimize so we do not need to regenerate data object when key
  //  order changes...will require hardcoding checking for the headerRecord
  //  in selection logic
  const dataObj: TableV2DataObject = useMemo(() => {
    return {
      ...inputData,
      [TABLE_V2_SELECT_ALL_KEY]: headerRecord,
    };
  }, [inputData, headerRecord]);

  const getRowByKey = useCallback(
    (rowKey: string): TableV2RecordType | undefined => {
      return dataObj[rowKey];
    },
    [dataObj],
  );

  // Store an array of keys for selected rows.
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // store a set of unique keys, where the special _all indicator is excluded
  const [selectedRowsUnique, setSelectedRowsUnique] = useState<Set<string>>(
    new Set(),
  );

  const updateSelectedRows = useCallback(
    (nextSelectedRows: string[]) => {
      setSelectedRows(nextSelectedRows);
      const uniqueRows = new Set(nextSelectedRows);
      uniqueRows.delete(TABLE_V2_SELECT_ALL_KEY);
      setSelectedRowsUnique(uniqueRows);
    },
    [setSelectedRows, setSelectedRowsUnique],
  );
  const deselectAll = useCallback(() => {
    updateSelectedRows([]);
  }, [updateSelectedRows]);
  const selectAll = useCallback(() => {
    const childKeys = getNestedChildKeysFromRecordDepthFirst(
      headerRecord,
      disabledOrOmittedRows,
    );
    updateSelectedRows(childKeys);
  }, [headerRecord, disabledOrOmittedRows, updateSelectedRows]);

  const [prevOrderedKeys, setPrevOrderedKeys] = useState(orderedKeys);
  if (orderedKeys !== prevOrderedKeys) {
    setPrevOrderedKeys(orderedKeys);
    updateSelectedRows(
      selectedRows.filter((rowKey) => orderedKeys.includes(rowKey)),
    );
  }

  // Store the most recently selected row for use in range selection.
  const [lastSelected, setLastSelected] = useState<string | null>(null);

  // Track if the shift key is pressed, to provide range selection behavior.
  const shiftPressed = useKeyPress('Shift');
  return (
    <TableV2SelectionContext.Provider
      value={{
        dataObj,
        orderedKeys,
        headerRecord,
        selectedRows,
        selectedRowsUnique,
        disabledOrOmittedRows,
        shiftPressed,
        lastSelected,
        setLastSelected,
        updateSelectedRows,
        selectAll,
        deselectAll,
        getRowByKey,
      }}
    >
      {children}
    </TableV2SelectionContext.Provider>
  );
};

export const useTableV2SelectionContext = () => {
  return useContext(TableV2SelectionContext);
};
